import React from 'react'

import Category from '../../components/Category/index'
import NotFound from '../../components/NotFound'
import { logServerSidePropsError } from '../../components/utilities/log-helper'
import fetchQueryWrapper from '../../core/fetchQueryWrapper'
import extractCollectionInternals from '../../data/transformers/extractCollectionInternals'
import extractStoryCollection from '../../data/transformers/extractStoryCollection'
import { RouteRedMetrics } from '../../utils/metrics'

const CategoryPage = ({ data, error, params, metadata, hero, body }) => {
  if (error) {
    return <NotFound />
  }

  return <Category data={data} params={params} metadata={metadata} hero={hero} body={body} />
}

export const paramsForCollection = (slug, regionId) => ({
  content_type: 'collection',
  include: 1,
  'fields.slug': slug,
  ...(regionId && { 'fields.regions.sys.id[in]': regionId })
})

export const paramsForStoryCollection = (categories, regions, authorId, limit = 6) => ({
  content_type: 'story',
  include: 1,
  skip: 0,
  limit,
  ...(regions ? { 'fields.regions.sys.id[in]': regions } : {}),
  ...(categories ? { 'fields.categories.sys.id[in]': categories } : {}),
  ...(authorId ? { 'fields.authors.sys.id': authorId } : {}),
  order: '-fields.publishedAt'
})

export async function getServerSideProps(context) {
  const redMetrics = new RouteRedMetrics({
    method: 'GET',
    route: `category/x`
  }).start()

  try {
    const { slug } = context.params
    if (!slug) {
      return { props: {} }
    }

    const collectionInternals = await fetchQueryWrapper(paramsForCollection(slug), extractCollectionInternals)
    if (collectionInternals.categories.length === 0) {
      redMetrics.end({
        status: 404,
        error: 'Fetching collection for slug failed for category page'
      })
      return { notFound: true }
    }

    const categories = collectionInternals.categories?.join(',') || ''
    const regions = collectionInternals.regions?.join(',') || ''
    context.res.chartbeatSections = collectionInternals.categoryNames.join(',')
    context.res.chartbeatType = 'collection'

    const storyCollectionParams = paramsForStoryCollection(categories, regions)

    const data = await fetchQueryWrapper(storyCollectionParams, extractStoryCollection)

    const surrogateKeyHeader = context?.res?.getHeader('Surrogate-Key')?.split(' ')
    const surrogateKeys = [...surrogateKeyHeader, ...data?.storyIDs, collectionInternals?.id, ...categories?.split(',')]
      .filter(Boolean)
      .join(' ')
    context?.res?.setHeader('Surrogate-Key', surrogateKeys)

    redMetrics.end({
      status: 200
    })

    return {
      props: {
        data,
        params: { categories, regions },
        body: collectionInternals?.body,
        metadata: collectionInternals.metadata,
        hero: collectionInternals?.hero
      }
    }
  } catch (error) {
    logServerSidePropsError(error)
    redMetrics.end({
      status: 500,
      error: 'Failed to load category'
    })
    return { props: { error } }
  }
}

export default CategoryPage
